import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getFeatureFlags } from '../actions';
import FigoLoadingOverlay from '../components/common/FigoLoadingOverlay';

const FeatureFlagContext = createContext();

export const FeatureFlagProvider = ({ children }) => {
  const {
    flags,
    loading,
  } = useSelector(({ settings }) => settings.featureFlags, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getFeatureFlags());
  }, [dispatch]);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {loading ? <FigoLoadingOverlay visible /> : null}
      {!loading && flags ? children : null}
    </FeatureFlagContext.Provider>
  );
};

const useFeatureFlag = (featureKey) => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw Error('Feature Flag Context not found');
  }

  const featureFlags = context;
  const featureFlag = useMemo(() => !!featureFlags[featureKey], []);
  return featureFlag;
};

const useSubmitRequestAccountChanges =
  () => useFeatureFlag('PetCloudSubmitForm');
const useWebViewReskin = () => useFeatureFlag('PetCloudWebViewReskin');
const useClaimApiV2 = () => useFeatureFlag('PetCloudClaimsApiV2');
const useManualVet = () => useFeatureFlag('PetCloudManualVetFlow');
const useCustomerV1T176413 = () => useFeatureFlag('PetCloudCustomerV1T176413');
const useAddPetBanner = () => useFeatureFlag('PetCloudAddPetBannerT188059');
const useCancelRenewalFigo = () => useFeatureFlag('PetCloudCancelRenewalFigo');
const useDirectCancel = () => useFeatureFlag('PetCloudT198497DirectCancel');
const useRecaptcha = () => useFeatureFlag('PetCloudRecaptcha');
const useCareCreditReimbursement =
  () => useFeatureFlag('PetCloudCareCreditReimbursement');
const useComboBoxLanguage = () => useFeatureFlag(
  'PetCloudLanguageComboT164558',
);
const useFileClaimPPUByPass = () => useFeatureFlag('PetCloudT214041PPUClaims');
const usePendingDemographics = () => useFeatureFlag(
  'PetCloudPendingDemographics',
);
const useOtpSMSFlow = () => useFeatureFlag('PetCloudRequireSMSVerification');
const useReimbursementVerification =
  () => useFeatureFlag('PetCloudRequireReimbursementVerification');

const useEditReimbursementVerification =
  () => useFeatureFlag('CrossAppValidateOpenClaimsWhenChangingRM');

const useReimbursementVerificationPPU =
  () => useFeatureFlag('PetCloudRequireReimbursementVerificationPPU');
const useReimbursementVerificationPPUPetCare =
  () => useFeatureFlag('PetCloudRequireReimbursementVerificationPPUPetCare');
const useReimbursementVerificationSpotCanada =
  () => useFeatureFlag('PetCloudRequireReimbursementVerificationSpotCanada');
const useSMSVerificationPPU =
  () => useFeatureFlag('PetCloudRequireSMSVerificationPPU');
const useSMSVerificationPPUPetCare =
  () => useFeatureFlag('PetCloudRequireSMSVerificationPPUPetCare');
const useSMSVerificationSpotCanada =
  () => useFeatureFlag('PetCloudRequireSMSVerificationSpotCanada');
const useWACostcoValidation =
  () => useFeatureFlag('PetCloudRequireWACostcoValidation');

const useReimbursementMethodDisclaimer = () => useFeatureFlag(
  'PetCloudReimbursementMethodDisclaimer',
);
const usePaymentech = () => useFeatureFlag('PetCloudPaymentech');
const useMoneris = () => useFeatureFlag('PetCloudMoneris');

const useOrcaChangeCoverage =
  () => useFeatureFlag('PetCloudOrcaChangeCoverage');

const useSuccessScreenChangeCoverage =
  () => useFeatureFlag('PetCloudSuccessScreenChangeCoverage');
const usePetcloudCCProp103 = () => useFeatureFlag('PetCloudCCProp103');

export {
  useAddPetBanner,
  useCancelRenewalFigo,
  useCareCreditReimbursement,
  useClaimApiV2,
  useComboBoxLanguage,
  useCustomerV1T176413,
  useDirectCancel,
  useEditReimbursementVerification,
  useFileClaimPPUByPass,
  useManualVet,
  useMoneris,
  useOrcaChangeCoverage,
  useOtpSMSFlow,
  usePaymentech,
  usePendingDemographics,
  usePetcloudCCProp103,
  useRecaptcha,
  useReimbursementMethodDisclaimer,
  useReimbursementVerification,
  useReimbursementVerificationPPU,
  useReimbursementVerificationPPUPetCare,
  useReimbursementVerificationSpotCanada,
  useSMSVerificationPPU,
  useSMSVerificationPPUPetCare,
  useSMSVerificationSpotCanada,
  useSubmitRequestAccountChanges,
  useSuccessScreenChangeCoverage,
  useWACostcoValidation,
  useWebViewReskin,
};
